import {Toaster} from '@/shared/ui/toaster';
import {Toaster as Sonner} from '@/shared/ui/sonner';
import {TooltipProvider} from '@/shared/ui/tooltip';
import {PageLoader} from '@/shared/ui/page-loader';
import {QueryClient, QueryClientProvider, useQueryClient} from '@tanstack/react-query';
import {BrowserRouter, Routes, Route, Outlet, useParams} from 'react-router-dom';
import {AuthProvider, useAuth} from '@/contexts/AuthContext';
import {lazy as reactLazy, Suspense, useState, useEffect, ComponentType} from 'react';

const lazy = <T extends ComponentType<any>>(factory: () => Promise<{default: T}>) =>
  reactLazy(() =>
    factory().catch((err) => {
      const msg = err?.message || '';
      const isChunkError =
        err?.name === 'ChunkLoadError' ||
        /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/.test(msg);
      if (isChunkError && !sessionStorage.getItem('chunk-reload')) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        return new Promise<{default: T}>(() => {});
      }
      throw err;
    })
  );
import {participantsApi} from '@/features/participants/services';
import ParticipantProjectView from '@/features/participants/components/ParticipantProjectView';
import {projectsApi} from '@/features/projects/services';
import {queryKeys} from '@/lib/query-keys';
import {CACHE_TTL} from '@/config/cacheTtl';
import {HomeIdsProvider} from '@/contexts/HomeIdsContext';
import {UploadProvider} from '@/contexts/UploadContext';
import {AuthRedirect, AuthGuard, RoleGuard, MasterInventoryGuard, SSOHandler, RoleRedirect} from '@/features/auth/components';
import Layout from './components/layout/Layout';
import ParticipantProjectLayout from './components/layout/ParticipantProjectLayout';
import ProjectsContextProvider from './contexts/ProjectsContext';
import Auth from './pages/Auth';
import SSOCallback from './pages/SSOCallback';
import NotFound from './pages/NotFound';

const UnderDevelopment = lazy(() => import('@/components/UnderDevelopment'));
const Projects = lazy(() => import('./pages/Projects'));
const TasksDataFields = lazy(() => import('./pages/TasksDataFields'));
const TaskViewTemplateBuilder = lazy(() => import('./pages/TaskViewTemplateBuilder'));
const TaskDataFieldsTemplateBuilder = lazy(() => import('./pages/TaskDataFieldsTemplateBuilder'));
const AddTaskDataFields = lazy(() => import('./pages/AddTaskDataFields'));
const AddTask = lazy(() => import('./pages/AddTask'));
const Moderators = lazy(() => import('./pages/Moderators'));
const Participants = lazy(() => import('./pages/Participants'));
const InviteParticipants = lazy(() => import('./pages/InviteParticipants'));
const SurveyUpload = lazy(() => import('./pages/SurveyUpload'));
const JournalEntry = lazy(() => import('./pages/JournalEntry'));
const ProjectModeratorMapping = lazy(() => import('./pages/ProjectModeratorMapping'));
const ProjectModeratorAssignment = lazy(() => import('./pages/ProjectModeratorAssignment'));
const Schedule = lazy(() => import('./pages/Schedule'));
const PMBookSession = lazy(() => import('./pages/PMBookSession'));
const PMSchedules = lazy(() => import('./pages/PMSchedules'));
const PMSessionCheckIn = lazy(() => import('./pages/PMSessionCheckIn'));
const PMSessionStarted = lazy(() => import('./pages/PMSessionStarted'));
const PMAddSubmitData = lazy(() => import('./pages/PMAddSubmitData'));
const ModeratorDashboard = lazy(() => import('./pages/ModeratorDashboard'));
const ModeratorTasks = lazy(() => import('./pages/ModeratorTasks'));
const ModeratorAvailability = lazy(() => import('./pages/ModeratorAvailability'));
const ModeratorParticipants = lazy(() => import('./pages/ModeratorParticipants'));
const ModeratorSchedules = lazy(() => import('./pages/ModeratorSchedules'));
const SurveyData = lazy(() => import('./pages/SurveyData'));
const Sessions = lazy(() => import('./pages/Sessions'));
const RoleManagement = lazy(() => import('./pages/RoleManagement'));
const AcceptTeamInvitation = lazy(() => import('./pages/AcceptTeamInvitation'));
const PendingApproval = lazy(() => import('./pages/PendingApproval'));
const MultimediaFiles = lazy(() => import('./pages/MultimediaFiles'));
const DataCollectionUploads = lazy(() => import('./pages/DataCollectionUploads'));
const ParticipantSchedulePage = lazy(() => import('./pages/ParticipantSchedulePage'));
const ParticipantBookSession = lazy(() => import('./pages/ParticipantBookSession'));
const SessionCheckIn = lazy(() => import('./pages/SessionCheckIn'));
const ParticipantAddSubmitData = lazy(() => import('./pages/ParticipantAddSubmitData'));
const ParticipantSubmitData = lazy(() => import('./pages/ParticipantSubmitData'));
const ParticipantSessionStarted = lazy(() => import('./pages/ParticipantSessionStarted'));
const DataCollectionSession = lazy(() => import('./pages/DataCollectionSession'));
const BulkUpload = lazy(() => import('./pages/BulkUpload'));
const Profile = lazy(() => import('./pages/Profile'));
const InventoryManagementPage = lazy(() => import('./pages/InventoryManagement'));
const PublicBookingPage = lazy(() => import('./pages/PublicBookingPage'));
const ParticipantLanding = lazy(() => import('./pages/ParticipantLanding'));


// Exported so tests can clear cross-test cache pollution between cases that
// reuse the same id (e.g. project/schedule detail queries) — not consumed
// by any other app code, which should go through useQueryClient() instead.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Wrapper component to fetch project data for participant by email
const ParticipantProjectViewWrapper = () => {
  const {user} = useAuth();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      if (!user?.email) return;

      try {
        // Get participant data
        const participantResponse = await participantsApi.getByEmail(user.email);
        if (!participantResponse.data || !participantResponse.data.project_id) {
          setLoading(false);
          return;
        }

        // Get project data
        const projectResponse = await projectsApi.getProject(participantResponse.data.project_id);
        if (projectResponse.data && projectResponse.data.data) {
          setProject(projectResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [user]);

  if (loading) {
    return <PageLoader message="Loading" />;
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No project assigned</p>
      </div>
    );
  }

  return <ParticipantProjectView project={project} />;
};

// Wrapper component to fetch project data by project ID from route params
const ParticipantProjectViewByID = () => {
  const {projectId} = useParams<{projectId: string}>();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setError('Project ID is required');
        setLoading(false);
        return;
      }

      try {
        const projectResponse = await queryClient.fetchQuery({
          queryKey: queryKeys.projects.detail(projectId),
          queryFn: () => projectsApi.getProject(projectId),
          staleTime: CACHE_TTL.REFERENCE,
          // Preserve the pre-caching behavior: a one-shot page load fails
          // fast rather than inheriting the app-wide retry:1 default, which
          // would add a real delay (~1s backoff) to every error case here,
          // including non-transient ones (e.g. permission denials) that a
          // retry can't fix anyway.
          retry: false,
        });
        if (projectResponse.data && projectResponse.data.data) {
          setProject(projectResponse.data.data);
        } else {
          setError('Project not found');
        }
      } catch (error: any) {
        console.error('Error fetching project:', error);
        setError(error.response?.data?.error || 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, queryClient]);

  if (loading) {
    return <PageLoader message="Loading" />;
  }

  if (error || !project) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{error || 'Project not found'}</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      <ParticipantProjectView project={project} />
    </div>
  );
};

// Shared layout with a single Navigation and an Outlet for child pages

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ProjectsContextProvider>
        <HomeIdsProvider>
          <UploadProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AuthRedirect>
                  <Suspense fallback={<PageLoader message="Loading..." fullScreen />}>
                  <Routes>
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<SSOCallback />} />
                    <Route path="/accept-team-invitation" element={<AcceptTeamInvitation />} />
                    {/* Public booking route - no auth required */}
                    <Route path="/book/:token" element={<PublicBookingPage />} />
                    <Route
                      path="/pending-approval"
                      element={<PendingApproval />}
                    />

                    {/* Layout for PM/Moderator side with a single Navigation */}
                    <Route
                      path="/"
                      element={
                        <>
                          <SSOHandler />
                          <RoleRedirect />
                          <AuthGuard>
                            <RoleGuard
                              allowedRoles={['admin', 'project_manager', 'scheduler', 'moderator']}
                            >
                              <Layout />
                            </RoleGuard>
                          </AuthGuard>
                        </>
                      }
                    >
                      <Route
                        index
                        element={
                          <UnderDevelopment
                            moduleName="Home"
                            breadcrumbRight="Home"
                          />
                        }
                      />
                      <Route path="projects/submit-data" element={<PMAddSubmitData />} />
                      <Route path="projects/bulk-upload" element={<BulkUpload />} />
                      <Route path="projects" element={<Projects />} />
                      <Route path="tasks" element={<TasksDataFields />} />
                      <Route
                        path="tasks/add"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <AddTaskDataFields />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tasks/:id/edit"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <AddTaskDataFields />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tasks/data-fields-template"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <TaskDataFieldsTemplateBuilder />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tasks/task-view-template"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <TaskViewTemplateBuilder />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tasks/add-manual"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <AddTask />
                          </RoleGuard>
                        }
                      />
                      <Route
                        path="tasks/add-manual/:id/edit"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <AddTask />
                          </RoleGuard>
                        }
                      />
                      <Route path="participants" element={<Participants />} />
                      <Route path="invite-participants" element={<InviteParticipants />} />
                      {/* Master inventory: admins (viewing as admin) and users
                          who are a PM on at least one project. Login role alone
                          is not enough — Centific users default to PM. */}
                      <Route
                        path="inventory"
                        element={
                          <RoleGuard allowedRoles={['admin', 'project_manager']}>
                            <MasterInventoryGuard>
                              <InventoryManagementPage />
                            </MasterInventoryGuard>
                          </RoleGuard>
                        }
                      />
                      <Route path="survey-upload" element={<SurveyUpload />} />
                      <Route path="journal-entry" element={<JournalEntry />} />
                      <Route
                        path="mapping"
                        element={<ProjectModeratorMapping />}
                      />
                      <Route
                        path="moderator-assignments"
                        element={<ProjectModeratorAssignment />}
                      />
                      <Route
                        path="schedules"
                        element={<Schedule />}
                      />
                      <Route
                        path="schedules/book"
                        element={<PMBookSession />}
                        />
                      <Route
                        path="schedules/check-in"
                        element={<PMSessionCheckIn />}
                      />
                      <Route
                        path="schedules/session"
                        element={<PMSessionStarted />}
                      />
                      <Route
                        path="schedules/data-collection-session"
                        element={<DataCollectionSession />}
                      />
                      <Route
                        path="moderator"
                        element={<ModeratorDashboard />}
                      />
                      <Route
                        path="moderator/data-collection"
                        element={<DataCollectionUploads />}
                      />
                      <Route
                        path="moderator/tasks"
                        element={<ModeratorTasks />}
                      />
                      <Route
                        path="moderator/availability"
                        element={<ModeratorAvailability />}
                      />
                      <Route
                        path="moderator/participants"
                        element={<ModeratorParticipants />}
                      />
                      <Route
                        path="moderator/schedules"
                        element={<ModeratorSchedules />}
                      />
                      <Route path="survey-data" element={<SurveyData />} />
                      <Route path="sessions" element={<Sessions />} />
                      <Route
                        path="role-management"
                        element={<RoleManagement />}
                      />
                      <Route path="moderators" element={<Moderators />} />
                      <Route
                        path="multimedia-files"
                        element={<MultimediaFiles />}
                      />
                      <Route
                        path="surveys"
                        element={
                          <div className="p-6">
                            <h1 className="text-2xl font-bold mb-4">Surveys</h1>
                            <p>Survey management page - coming soon</p>
                          </div>
                        }
                      />
                      <Route
                        path="feedback"
                        element={
                          <UnderDevelopment
                            moduleName="Help & FAQ"
                            breadcrumbRight="Help & Support"
                          />
                        }
                      />
                      <Route
                        path="calendar"
                        element={
                          <UnderDevelopment
                            moduleName="My Calendar"
                            breadcrumbRight="Calendar"
                          />
                        }
                      />
                      <Route
                        path="analytics"
                        element={
                          <UnderDevelopment
                            moduleName="Reports / Dashboards"
                            breadcrumbRight="Reports / Dashboards"
                          />
                        }
                      />
                      <Route path="profile" element={<Profile />} />
                      <Route
                        path="account-settings"
                        element={
                          <UnderDevelopment
                            moduleName="Account Settings"
                            breadcrumbRight="Account Settings"
                          />
                        }
                      />
                    </Route>

                    {/* Participant routes with project navigation */}
                    <Route
                      path="/participant"
                      element={
                        <AuthGuard>
                          <RoleGuard allowedRoles={['participant']}>
                            <ParticipantProjectLayout />
                          </RoleGuard>
                        </AuthGuard>
                      }
                    >
                      {/* Participant Home (overview) */}
                      <Route index element={
                        <UnderDevelopment
                          moduleName="Home"
                          breadcrumbRight="Home"
                        />
                      } />
                      
                      {/* Project Sub-routes with project ID */}
                      <Route path="project/:projectId/overview" element={<ParticipantProjectViewByID />} />
                      <Route path="project/:projectId/training" element={
                        <UnderDevelopment
                          moduleName="Training Materials"
                          breadcrumbRight="Training"
                        />
                      } />
                      <Route path="project/:projectId/schedule" element={<ParticipantSchedulePage />} />
                      <Route path="project/:projectId/book-session" element={<ParticipantBookSession />} />
                      <Route path="project/:projectId/check-in" element={<SessionCheckIn />} />
                      <Route path="project/:projectId/session-started" element={<ParticipantSessionStarted />} />
                      <Route path="project/:projectId/submit-data" element={<ParticipantSubmitData />} />
                      <Route path="project/:projectId/add-submit-data" element={<ParticipantAddSubmitData />} />
                      <Route path="project/:projectId/bulk-upload" element={<BulkUpload />} />
                      
                      {/* Project Sub-routes without project ID (fallback) */}
                      <Route path="project/overview" element={
                        <div className="max-w-7xl">
                          {/* ParticipantProjectView will be wrapped with data fetching */}
                          <ParticipantProjectViewWrapper />
                        </div>
                      } />
                      <Route path="project/training" element={
                        <UnderDevelopment
                          moduleName="Training Materials"
                          breadcrumbRight="Training"
                        />
                      } />
                      <Route path="project/schedule" element={<ParticipantSchedulePage />} />
                      <Route path="project/book-session" element={<ParticipantBookSession />} />
                      <Route path="project/submit-data" element={<ParticipantSubmitData />} />
                      <Route path="project/check-in" element={<SessionCheckIn />} />
                      <Route path="project/session-started" element={<ParticipantSessionStarted />} />
                      <Route path="project/add-submit-data" element={<ParticipantAddSubmitData />} />
                      
                      {/* Other participant routes */}
                      <Route path="calendar" element={
                        <UnderDevelopment
                          moduleName="My Calendar"
                          breadcrumbRight="Calendar"
                        />
                      } />
                      <Route path="support" element={
                        <UnderDevelopment
                          moduleName="Support & FAQ"
                          breadcrumbRight="Support"
                        />
                      } />
                      <Route path="profile" element={<Profile />} />
                      <Route path="account-settings" element={
                        <UnderDevelopment
                          moduleName="Account Settings"
                          breadcrumbRight="Account Settings"
                        />
                      } />
                    </Route>
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </AuthRedirect>
              </BrowserRouter>
            </TooltipProvider>
          </UploadProvider>
        </HomeIdsProvider>
      </ProjectsContextProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
